import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { ComparisonMeta } from '../reports/Statistics/Statistics.hooks';
import { chartPalette } from './chartPalette';

type DashboardMode = 'light' | 'dark';

export interface TopProductsComparisonChartProps {
  data: Array<Record<string, unknown>>;
  comparisonMeta: ComparisonMeta;
  loading?: boolean;
  mode?: DashboardMode;
}

const ChartContainer = styled(Paper, { shouldForwardProp: (p) => p !== 'mode' })<{
  mode?: DashboardMode;
}>(({ mode = 'dark' }) => {
  const isLight = mode === 'light';
  return {
    padding: '24px',
    borderRadius: '16px',
    position: 'relative',
    overflow: 'hidden',
    ...(isLight
      ? {
          background: '#ffffff',
          border: '1px solid #bae6fd',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          '&::before': { content: 'none' },
        }
      : {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
          background:
            'linear-gradient(135deg, rgba(15, 23, 42, 0.8) 0%, rgba(30, 41, 59, 0.8) 100%)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.05) 0%, transparent 50%)',
            pointerEvents: 'none',
            zIndex: 0,
          },
        }),
  };
});

const Title = styled(Typography, { shouldForwardProp: (p) => p !== 'mode' })<{
  mode?: DashboardMode;
}>(({ mode = 'dark' }) => ({
  fontSize: '20px',
  fontWeight: 600,
  color: mode === 'light' ? '#0f172a' : '#ffffff',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  letterSpacing: '-0.01em',
  marginBottom: '24px',
  position: 'relative',
  zIndex: 1,
}));

export const TopProductsComparisonChart: React.FC<TopProductsComparisonChartProps> = ({
  data,
  comparisonMeta,
  loading = false,
  mode = 'dark',
}) => {
  const isLight = mode === 'light';
  const gridStroke = isLight ? '#e2e8f0' : 'rgba(255, 255, 255, 0.05)';
  const axisStroke = isLight ? '#94a3b8' : 'rgba(255, 255, 255, 0.4)';
  const tickFill = isLight ? '#64748b' : 'rgba(255, 255, 255, 0.6)';

  const periodKeys = comparisonMeta.periodKeys;

  const periodTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    periodKeys.forEach((k) => {
      totals[k] = 0;
    });
    data.forEach((row) => {
      periodKeys.forEach((k) => {
        totals[k] = (totals[k] ?? 0) + ((row[k] as number) ?? 0);
      });
    });
    return totals;
  }, [data, periodKeys]);

  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload?: Record<string, unknown> }>;
  }) => {
    if (!active || !payload?.length) return null;
    const point = (payload[0]?.payload ?? payload[0]) as Record<string, unknown>;
    const productName = String(point.productName ?? point.displayName ?? '');
    return (
      <Box
        sx={
          isLight
            ? {
                background: '#ffffff',
                border: '1px solid #e0f2fe',
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }
            : {
                background:
                  'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(10px)',
              }
        }
      >
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            color: isLight ? '#64748b' : '#ffffff',
            mb: 1,
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {productName}
        </Typography>
        {comparisonMeta.periodLabels.map((label, i) => {
          const key = periodKeys[i];
          const val = (point[key] as number) ?? 0;
          const color = chartPalette.series[i % chartPalette.series.length];
          return (
            <Typography
              key={key}
              sx={{ fontSize: '13px', color, fontWeight: 600, fontFamily: 'Inter, sans-serif' }}
            >
              {label}: {(val as number).toLocaleString('es-BO')} und.
            </Typography>
          );
        })}
      </Box>
    );
  };

  if (loading) {
    return (
      <ChartContainer mode={mode}>
        <Box
          sx={{ height: '320px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Typography sx={{ color: isLight ? '#94a3b8' : 'rgba(255, 255, 255, 0.5)' }}>
            Cargando...
          </Typography>
        </Box>
      </ChartContainer>
    );
  }

  if (!data?.length) {
    return (
      <ChartContainer mode={mode}>
        <Title mode={mode}>Top productos - Comparación por período</Title>
        <Box
          sx={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Typography sx={{ color: isLight ? '#94a3b8' : 'rgba(255, 255, 255, 0.5)' }}>
            No hay datos para comparar
          </Typography>
        </Box>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer mode={mode}>
      <Title mode={mode}>Top productos - Comparación por período</Title>
      <Box
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, position: 'relative', zIndex: 1 }}
      >
        {periodKeys.map((key, i) => {
          const total = periodTotals[key] ?? 0;
          const color = chartPalette.series[i % chartPalette.series.length];
          const label = comparisonMeta.periodLabels[i];
          return (
            <Box
              key={key}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                bgcolor: isLight ? 'grey.50' : 'rgba(255,255,255,0.05)',
                border: '1px solid',
                borderColor: isLight ? 'grey.200' : 'rgba(255,255,255,0.1)',
              }}
            >
              <Typography sx={{ fontSize: '11px', color: tickFill, fontWeight: 500 }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: '15px', fontWeight: 700, color }}>
                {total.toLocaleString('es-BO')} und.
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <ResponsiveContainer width="100%" height={Math.max(280, data.length * 50)}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 10, right: 50, left: 20, bottom: 5 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={gridStroke}
              horizontal={true}
              vertical={false}
            />
            <XAxis
              type="number"
              stroke={axisStroke}
              style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
              tick={{ fill: tickFill }}
              axisLine={{ stroke: axisStroke }}
              tickFormatter={(v) => Number(v).toLocaleString('es-BO')}
            />
            <YAxis
              type="category"
              dataKey="displayName"
              stroke={axisStroke}
              style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
              tick={{ fill: tickFill }}
              axisLine={{ stroke: axisStroke }}
              width={180}
              tickLine={false}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255, 255, 255, 0.05)' }}
            />
            <Legend
              wrapperStyle={{ position: 'relative' }}
              formatter={(value) => (
                <span style={{ color: tickFill, fontSize: '12px' }}>{value}</span>
              )}
            />
            {periodKeys.map((key, i) => (
              <Bar
                key={key}
                dataKey={key}
                name={comparisonMeta.periodLabels[i]}
                fill={chartPalette.series[i % chartPalette.series.length]}
                radius={[0, 4, 4, 0]}
                animationDuration={600}
                animationEasing="ease-out"
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </ChartContainer>
  );
};
